import React, { Fragment, useEffect, useState } from 'react'
import { startCase } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import pluralize from 'pluralize'
import queryString from 'query-string'
import { IconHeadphones, IconHeadphonesOff, IconMessage } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { UserInitial } from '@/components/UserInitial'
import {
  attemptListeningToCall,
  stopListeningToCall,
} from '@/reducers/commandCenter/commandCenter.redux'
import { fetchAgentWithCallStatus } from '@/reducers/commandCenter/commandCenter.actions'
import { stopListeningToAlert } from '@/reducers/realtime/realtime.actions'
import {
  BEGIN_CHAT,
  OPEN_ASSIST_PANEL,
  SET_ACTIVE_USERNAME,
} from '@/reducers/realtime/realtimeChats.redux'
import { ExternalLink } from '@/components/ExternalLink'
import { Pill } from '@/components/pills/Pill'
import { formatTime } from '@/utils/helpers'

import { ListeningStatus } from './ListeningStatus'
import { getAgentAudioIssueDetails } from '../commandCenter.helpers'
import { TimeElapsed } from './TimeElapsed'
import { SoundWaveAnimation } from './SoundWaveAnimation'

export const AgentCallStatusEntry = ({ agent, index }) => {
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState(false)
  const { listeningToAgent, agentsAudioIssues } = useSelector((state) => state.commandCenter)
  const { listeningToUsername } = useSelector((state) => state.realtimeUserAlerts)
  const chats = useSelector((state) => state.realtimeChats.chats)
  const {
    organizationid: organizationId,
    username: currentUser,
    user_id: managerId,
    first_name: managerFirstName,
    last_name: managerLastName,
  } = useSelector((state) => state.currentUser)
  const [callIdsRetrieved, setCallIdsRetrieved] = useState([])
  const agentFullName = `${agent.agent_first_name} ${agent.agent_last_name}`
  const userHadCalls = agent.call_data?.call_status !== 'no_calls_today'
  const agentIsOnline = agent.agent_status === 'online' || agent.agent_status === 'on_call'
  // Either surface can start the listen — the row from Live Listen, the Agent
  // Assist widget from a notification — and they record it in different slices.
  const isListeningToAgent =
    listeningToAgent === agent.agent_username || listeningToUsername === agent.agent_username
  const isActivelyListening = agent.call_data?.call_processing_url && isListeningToAgent

  // Stop whichever surface owns the listen. The row and the Agent Assist widget
  // record it in different slices, and clearing only one leaves a Stop button
  // over dead audio with no way back.
  const stopListening = () => {
    dispatch(stopListeningToCall())
    dispatch(stopListeningToAlert())
  }

  // this useEffect handles the case where you are listening to an agent, and the listen is stopped
  // from the BE. This will then clean up the listening state in the FE.
  //
  // Keyed on the ROW's own slice, not `isListeningToAgent`. That also covers a
  // listen started from the Agent Assist widget, whose target legitimately has
  // no roster URL — `isOnCall` accepts a live alert, and an alert carries its
  // own processing URL — so this fired on mount and killed the widget's audio.
  useEffect(() => {
    if (listeningToAgent === agent.agent_username && !agent.call_data?.call_processing_url) {
      stopListening()
    }
  }, [agent.call_data?.call_processing_url, listeningToAgent])

  useEffect(() => {
    if (userHadCalls && expanded && !callIdsRetrieved.includes(agent.call_data?.call_id)) {
      dispatch(fetchAgentWithCallStatus(agent.agent_id))
      setCallIdsRetrieved([...callIdsRetrieved, agent.call_data.call_id])
    }
  }, [expanded])

  const agentDetailsMap = [
    { label: 'Status', value: startCase(agent.agent_status) },
    { label: 'Name', value: `${agent.agent_first_name} ${agent.agent_last_name}` },
    { label: 'ID', value: agent.agent_username },
  ]

  const { audioIssues, callsWithIssues, hasAudioIssues } = getAgentAudioIssueDetails(
    agentsAudioIssues,
    agent.agent_id
  )

  if (hasAudioIssues) {
    const queryParams = queryString.stringify({ call_ids: callsWithIssues, organizationId })
    agentDetailsMap.push({
      label: 'Recent calls with issues',
      value: `${callsWithIssues.length} ${pluralize('Call', callsWithIssues.length)}`,
      link: `/calls?${queryParams}`,
    })
  }

  // Chat and Listen both hand this agent to the Agent Assist widget — it is the
  // single chat surface now, and it hosts the live transcript alongside.
  const openAgentAssist = () => {
    if (chats[agent.agent_username]) {
      dispatch({ type: SET_ACTIVE_USERNAME, activeUsername: agent.agent_username })
    } else {
      dispatch({
        type: BEGIN_CHAT,
        agentUsername: agent.agent_username,
        agentId: agent.agent_id,
        agentFirstName: agent.agent_first_name,
        agentLastName: agent.agent_last_name,
        managerId,
        managerUsername: currentUser,
        managerFirstName,
        managerLastName,
        alertId: null,
        callId: agent.call_data?.call_id || null,
      })
    }
    dispatch({ type: OPEN_ASSIST_PANEL })
  }

  const call_end_time = agent.call_data?.call_end_time
    ? formatTime(agent.call_data.call_end_time, 'h:mm A')
    : '--'

  const callDetailsMap = [
    {
      label: 'Status',
      value: startCase(agent.call_data?.call_status),
    },
    {
      label: 'Date',
      value: formatTime(agent.call_data?.call_start_time, 'MMMM Do, YYYY'),
    },
    {
      label: 'Start Time',
      value: formatTime(agent.call_data?.call_start_time, 'h:mm A'),
    },
    {
      label: 'End Time',
      value: call_end_time,
    },
    {
      label: 'Playbook',
      value: agent.call_data?.playbook_name,
      link: `/playbooks/${agent.call_data?.playbook_id}/checklist`,
    },
    {
      label: 'Call Details',
      value: 'Explore Call',
      link: `/calls/${agent.call_data?.call_id}`,
    },
    {
      label: 'Voip Customer ID',
      value: agent.call_data?.voip_customer_id || '--',
    },
    {
      label: 'Voip Campaign Name',
      value: agent.call_data?.voip_campaign_name || '--',
    },
  ]

  const detailsSections = [{ label: 'Agent Details', value: agentDetailsMap }]

  if (userHadCalls) {
    detailsSections.push({ label: 'Call Details', value: callDetailsMap })
  }

  return (
    <div
      className={classNames('agent-call-status-entry', {
        'actively-listening': isActivelyListening,
      })}
    >
      <div
        className="agent-call-status-entry__list"
        data-testid={`agent-call-status-entry-${agent.agent_id}`}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div className="agent-call-status-entry__agent">
          <InteractiveTooltip
            content={`${agentFullName} is ${agent.agent_status}`}
            placement="top-start"
          >
            <div className="relative">
              <UserInitial userFullName={agentFullName} index={index} />
              <ListeningStatus status={agent.agent_status} absolute />
            </div>
          </InteractiveTooltip>
          <h2>{agentFullName}</h2>
          {hasAudioIssues && (
            <InteractiveTooltip
              content={audioIssues.map((item) => (
                <div key={item}>{item}</div>
              ))}
            >
              <Pill
                critical
                outlined
                xs
                data-testid={`audio-issue-icon-${agent.agent_username}`}
                icon={<IconHeadphonesOff />}
              >
                {audioIssues.length}
              </Pill>
            </InteractiveTooltip>
          )}
        </div>
        <div className="flex-align-center gap">
          <TimeElapsed agent={agent} />
        </div>
        <div
          className="agent-call-status-entry__actions"
          onClick={(event) => {
            // don't toggle the panel when clicking on the action buttons
            event.stopPropagation()
          }}
        >
          <InteractiveTooltip
            content={agentIsOnline ? `Chat with ${agentFullName}` : `${agentFullName} is offline`}
            placement="top-end"
          >
            <Button
              square
              basic
              small
              disabled={!agentIsOnline}
              data-testid="cc-chat-icon"
              onClick={(event) => {
                event.stopPropagation()
                openAgentAssist()
              }}
            >
              <IconMessage />
            </Button>
          </InteractiveTooltip>
          {agent.call_data?.call_processing_url && !isListeningToAgent && (
            <Button
              square
              basic
              small
              data-testid="cc-listen-icon"
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                stopListening()
                dispatch(
                  attemptListeningToCall({
                    managerUsername: currentUser,
                    agentUsername: agent.agent_username,
                    processingUrl: agent.call_data.call_processing_url,
                    callId: agent.call_data.call_id,
                  })
                )
                openAgentAssist()
              }}
            >
              <IconHeadphones />
            </Button>
          )}
          {isActivelyListening && (
            <div className="command-center-listening-pill">
              <SoundWaveAnimation />
              Listening...
              <Button
                small
                destructive
                data-testid="cc-listen-stop-button"
                className="command-center-stop-listening__button"
                onClick={(event) => {
                  stopListening()
                  event.stopPropagation()
                }}
              >
                Stop
              </Button>
            </div>
          )}
          {!agent.call_data?.call_processing_url && (
            <InteractiveTooltip
              content={`${agentFullName} is not currently on a call`}
              placement="top-end"
            >
              <Button basic square small disabled data-testid="cc-listen-icon-disabled">
                <IconHeadphones />
              </Button>
            </InteractiveTooltip>
          )}
        </div>
      </div>
      {expanded && (
        <div
          className="agent-call-status-entry__details"
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <div className="agent-call-status-entry__expanded">
            <div className="agent-call-status-entry__expanded-details">
              {detailsSections.map((section) => (
                <div key={section.label}>
                  <h3>{section.label}</h3>
                  <div className="details-grid" data-testid="details-grid">
                    {section.value.map((detail) => (
                      <Fragment key={detail.label}>
                        <div>
                          <strong>{`${detail.label}:`}</strong>
                        </div>
                        {detail.link ? (
                          <ExternalLink
                            url={detail.link}
                            label={detail.value}
                            id={agent.call_data?.playbook_id}
                            wrap
                            onClick={(event) => {
                              event.stopPropagation()
                            }}
                          />
                        ) : (
                          <div>{detail?.value}</div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
